import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import { BROWSER_ICONS } from '../assets/index';

const tickerItems = [...BROWSER_ICONS, ...BROWSER_ICONS, ...BROWSER_ICONS, ...BROWSER_ICONS];

const getGap = () => {
  const w = window.innerWidth;
  if (w < 640) return 32;
  if (w < 1024) return 48;
  return 64;
};

const getIconSize = () => {
  const w = window.innerWidth;
  if (w < 640) return 28;
  if (w < 1024) return 40;
  return 48;
};

const Compatibility = () => {
  const { language } = useLanguage();
  const text = t[language].compatibility;
  const [gap, setGap] = useState(64);
  const [iconSize, setIconSize] = useState(48);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    const update = () => {
      setGap(getGap());
      setIconSize(getIconSize());
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const fontSize = iconSize < 32 ? '14px' : iconSize < 44 ? '18px' : '20px';
  const fontWeight = 700;

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-[#111111] border-y border-white/5 overflow-hidden flex flex-col items-center relative">
      <div className="container mx-auto px-4 sm:px-6 mb-8 sm:mb-12 relative z-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center text-xs sm:text-sm md:text-base font-bold text-gray-500 uppercase tracking-[0.2em]"
        >
          {text.title}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="relative w-full overflow-hidden py-6"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 md:w-48 bg-gradient-to-r from-[#111111] via-[#111111]/80 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 md:w-48 bg-gradient-to-l from-[#111111] via-[#111111]/80 to-transparent z-10 pointer-events-none" />

        <div
          ref={trackRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: 'max-content',
            animationPlayState: paused ? 'paused' : 'running',
          }}
          className="animate-ticker"
        >
          {[0, 1].map((setIdx) => (
            <div
              key={setIdx}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: `${gap}px`,
                flexShrink: 0,
                paddingRight: `${gap}px`,
              }}
            >
              {tickerItems.map((browser, idx) => (
                <BrowserItem
                  key={`${setIdx}-${idx}`}
                  browser={browser}
                  iconSize={iconSize}
                  fontSize={fontSize}
                  fontWeight={fontWeight}
                  gap={gap}
                />
              ))}
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const BrowserItem = ({ browser, iconSize, fontSize, fontWeight, gap }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      animate={{ scale: hovered ? 1.12 : 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: `${Math.round(gap * 0.4)}px`,
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      <img
        src={browser.icon}
        alt={browser.name}
        style={{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          objectFit: 'contain',
          filter: hovered
            ? 'drop-shadow(0 0 12px rgba(255,255,255,0.35))'
            : 'drop-shadow(0 0 4px rgba(255,255,255,0.05))',
          transition: 'filter 0.3s',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize,
          fontWeight,
          color: hovered ? '#ffffff' : '#d1d5db',
          textShadow: hovered ? '0 0 12px rgba(255,255,255,0.5)' : 'none',
          transition: 'color 0.3s, text-shadow 0.3s',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {browser.name}
      </span>
    </motion.div>
  );
};

export default Compatibility;